/**
 * 管理员套餐管理页面
 */
import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/adminApi';
import { confirm } from '../../store/toastStore';
import { PlanInfo } from '../../types';

const AdminPlans: React.FC = () => {
  const [plans, setPlans] = useState<PlanInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<PlanInfo | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // 表单字段
  const [form, setForm] = useState({
    plan_id: '',
    name: '',
    price: 0,
    duration_days: 30,
    description: '',
    is_active: true,
    sort_order: 0,
    max_per_user: '' as any,
    word_limit: '' as any,
  });

  const loadPlans = async () => {
    setLoading(true);
    try {
      const res = await adminApi.getPlans();
      setPlans(res.data);
    } catch {
      setError('加载套餐失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPlans(); }, []);

  const resetForm = () => {
    setForm({ plan_id: '', name: '', price: 0, duration_days: 30, description: '', is_active: true, sort_order: 0, max_per_user: '', word_limit: '' });
    setEditing(null);
    setShowCreate(false);
    setError('');
  };

  const handleEdit = (plan: PlanInfo) => {
    setEditing(plan);
    setForm({
      plan_id: plan.plan_id,
      name: plan.name,
      price: plan.price,
      duration_days: plan.duration_days,
      description: plan.description || '',
      is_active: plan.is_active,
      sort_order: plan.sort_order,
      max_per_user: plan.max_per_user ?? '',
      word_limit: plan.word_limit ?? '',
    });
    setShowCreate(false);
    setError('');
  };

  const handleSave = async () => {
    if (!form.plan_id || !form.name || form.price <= 0 || form.duration_days <= 0) {
      setError('请填写完整信息');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, max_per_user: form.max_per_user === '' ? null : form.max_per_user, word_limit: form.word_limit === '' ? null : form.word_limit };
      if (editing) {
        await adminApi.updatePlan(editing.id, payload);
      } else {
        await adminApi.createPlan(payload);
      }
      resetForm();
      await loadPlans();
    } catch (err: any) {
      setError(err.response?.data?.detail || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plan: PlanInfo) => {
    if (!await confirm({ title: '确认删除', message: `确定删除套餐「${plan.name}」吗？`, confirmLabel: '删除', danger: true })) return;
    try {
      await adminApi.deletePlan(plan.id);
      loadPlans();
    } catch (err: any) {
      setError(err.response?.data?.detail || '删除失败');
    }
  };

  const formatPrice = (price: number) => (price / 100).toFixed(2);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-foreground">套餐管理</h1>
        <button
          onClick={() => { resetForm(); setShowCreate(true); }}
          className="px-4 py-2 bg-primary hover:bg-primary/80 text-white text-sm font-semibold rounded-xl transition-colors"
        >
          + 新建套餐
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{error}</div>
      )}

      {/* 表单弹窗 */}
      {(showCreate || editing) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={resetForm} />
          <div className="relative bg-card rounded-2xl border border-border shadow-[0_20px_60px_rgba(0,0,0,0.15)] w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-foreground mb-4">
              {editing ? '编辑套餐' : '新建套餐'}
            </h2>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">套餐 ID</label>
                <input
                  value={form.plan_id}
                  onChange={(e) => setForm({ ...form, plan_id: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/40"
                  placeholder="如: 1month"
                  disabled={!!editing}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">名称</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/40"
                  placeholder="如: 1个月"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">价格（分）</label>
                  <input
                    type="number"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-border rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/40"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">天数</label>
                  <input
                    type="number"
                    value={form.duration_days}
                    onChange={(e) => setForm({ ...form, duration_days: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-border rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/40"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">每人限购次数 <span className="text-muted-foreground">(留空=不限)</span></label>
                <input
                  type="number"
                  value={form.max_per_user}
                  onChange={(e) => setForm({ ...form, max_per_user: e.target.value ? parseInt(e.target.value) : '' })}
                  className="w-full px-3 py-2 border border-border rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/40"
                  placeholder="不限"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground">字数额度</label>
                <input type="number" value={form.word_limit || ''} onChange={e => setForm({...form, word_limit: parseInt(e.target.value) || '' as any})}
                  placeholder="留空 = 不限" className="mt-1 block w-full rounded-md border-border shadow-sm sm:text-sm" />
                <p className="text-xs text-muted-foreground mt-1">单次购买可生成的字数，留空表示不限</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">描述</label>
                <input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/40"
                  placeholder="如: 19.9元/月"
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    className="w-4 h-4 text-primary border-border rounded"
                  />
                  启用
                </label>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-muted-foreground mb-1">排序</label>
                  <input
                    type="number"
                    value={form.sort_order}
                    onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
                    className="w-20 px-3 py-2 border border-border rounded-xl text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/40"
                  />
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={resetForm} className="px-4 py-2 text-sm text-foreground bg-accent rounded-xl hover:bg-accent">取消</button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-primary hover:bg-primary/80 text-white text-sm font-semibold rounded-xl disabled:opacity-50">
                {saving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 套餐列表 */}
      {loading ? (
        <div className="flex justify-center py-12 text-muted-foreground">加载中...</div>
      ) : (
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-background border-b border-border">
              <tr>
                <th className="text-left px-6 py-3 font-medium text-muted-foreground">ID</th>
                <th className="text-left px-6 py-3 font-medium text-muted-foreground">名称</th>
                <th className="text-left px-6 py-3 font-medium text-muted-foreground">价格</th>
                <th className="text-left px-6 py-3 font-medium text-muted-foreground">天数</th>
                <th className="text-left px-6 py-3 font-medium text-muted-foreground">限购</th>
                <th className="text-left px-6 py-3 font-medium text-muted-foreground">状态</th>
                <th className="text-right px-6 py-3 font-medium text-muted-foreground">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {plans.map((plan) => (
                <tr key={plan.id} className="hover:bg-accent">
                  <td className="px-6 py-3 text-foreground font-mono text-xs">{plan.plan_id}</td>
                  <td className="px-6 py-3 font-medium text-foreground">{plan.name}</td>
                  <td className="px-6 py-3 text-foreground">¥{formatPrice(plan.price)}</td>
                  <td className="px-6 py-3 text-foreground">{plan.duration_days}天</td>
                  <td className="px-6 py-3 text-muted-foreground">{plan.max_per_user ? `限${plan.max_per_user}次` : '不限'}</td>
                  <td className="px-6 py-3">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${plan.is_active ? 'bg-green-50 text-green-700' : 'bg-accent text-muted-foreground'}`}>
                      {plan.is_active ? '启用' : '禁用'}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-muted-foreground">{plan.sort_order}</td>
                  <td className="px-6 py-3 text-right">
                    <button onClick={() => handleEdit(plan)} className="text-primary hover:underline text-xs mr-3">编辑</button>
                    <button onClick={() => handleDelete(plan)} className="text-red-500 hover:underline text-xs">删除</button>
                  </td>
                </tr>
              ))}
              {plans.length === 0 && (
                <tr><td colSpan={7} className="px-6 py-12 text-center text-muted-foreground">暂无套餐</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminPlans;
